import React, { useMemo, useRef, useState } from 'react';
import Button from '../components/ui/Button';
import Popover from '../components/ui/Popover';
import TextInput from '../components/ui/TextInput';
import Tooltip from '../components/ui/Tooltip';
import { contributionValue, formatContribution } from './currentAttribution';

// Match the attribution summary's raw-value rule; displayed rounding must not
// disable a measured nonzero contribution.
export function factorInspectionReason(factor, mode) {
    const value = contributionValue(factor, mode);
    return value === 0 ? 'Nothing to inspect: this factor’s contribution is exactly zero for the selected window.'
        : value === null ? 'No contribution data are available for this factor in the selected window.' : null;
}

export default function FactorDetailSelector({ factors, value, mode, currency, onChange }) {
    const [search, setSearch] = useState('');
    const [menuWidth, setMenuWidth] = useState(300);
    const triggerRef = useRef(null);
    const listRef = useRef(null);
    const ordered = useMemo(() => [...factors].sort((a, b) => {
        const av = contributionValue(a, mode), bv = contributionValue(b, mode);
        return av === null ? bv === null ? a.factor.localeCompare(b.factor) : 1
            : bv === null ? -1 : Math.abs(bv) - Math.abs(av) || a.factor.localeCompare(b.factor);
    }), [factors, mode]);
    const selectedFactor = factors.find((factor) => factor.factor === value);
    const label = selectedFactor ? `${selectedFactor.factor} - ${selectedFactor.family || 'Other'}` : 'Choose factor';
    const query = search.trim().toLocaleLowerCase();
    const shown = ordered.filter((factor) => `${factor.factor} ${factor.family || ''}`.toLocaleLowerCase().includes(query));
    return <Popover aria-label="Choose a factor" className="box-border max-w-[calc(100vw-32px)] overflow-hidden p-2"
        onOpenChange={(open) => {
            if (open) {
                setSearch('');
                const width = triggerRef.current?.getBoundingClientRect().width;
                setMenuWidth(Math.max(0, Math.min(360, width || 320) - 20));
            }
        }}
        trigger={<Button ref={triggerRef} aria-label="Choose factor" className="w-full min-w-0 max-w-full justify-between gap-2 !rounded-none !border-0 !border-b-2 !border-solid !border-ui-link px-2 text-left">
            <span className="min-w-0 truncate" title={label}>{label}</span><span aria-hidden="true">⌄</span>
        </Button>}>
        {(close) => <div style={{ width: menuWidth, maxWidth: 'calc(100vw - 52px)', minWidth: 0 }}>
            <TextInput aria-label="Search inspector factors" placeholder="Search factors…" value={search}
                onChange={(event) => setSearch(event.target.value)}
                onKeyDown={(event) => {
                    if (event.key === 'ArrowDown') {
                        event.preventDefault();
                        listRef.current?.querySelector('button:not([disabled])')?.focus();
                    } else if (event.key === 'Enter') {
                        const match = shown.find((factor) => factor.factor.toLocaleLowerCase() === query) || (shown.length === 1 ? shown[0] : null);
                        if (match && !factorInspectionReason(match, mode)) { event.preventDefault(); onChange(match.factor); close(); }
                    }
                }} />
            <div className="py-1 text-[11px] text-content-secondary">{shown.length} / {factors.length} factors · contribution impact</div>
            <ul ref={listRef} aria-label="Factors by contribution impact" className="m-0 list-none p-0" style={{ maxHeight: 'min(320px, 40vh)', overflowY: 'auto', overflowX: 'hidden', minWidth: 0, maxWidth: '100%' }}>
                {shown.map((factor) => {
                    const reason = factorInspectionReason(factor, mode);
                    const button = <Button aria-label={`Inspect ${factor.factor}`} aria-pressed={value === factor.factor} disabled={Boolean(reason)}
                        className="w-full min-w-0 justify-between gap-2 px-2 text-left" onClick={() => { onChange(factor.factor); close(); }}>
                        <span className="min-w-0 truncate" title={factor.factor}>{factor.factor}</span>
                        <span className="shrink-0 font-normal tabular-nums">{formatContribution(contributionValue(factor, mode), mode, currency)}</span>
                    </Button>;
                    return <li key={factor.factor}>{reason ? <Tooltip content={reason}><span className="block min-w-0 text-ui-disabled-text" tabIndex={0} aria-disabled="true" title={reason}>{button}</span></Tooltip> : button}</li>;
                })}
            </ul>
            {!shown.length && <p className="my-2 text-[12px] text-content-secondary">No matching factors.</p>}
        </div>}
    </Popover>;
}
